import type { User } from '@/types';

export function formatTimestamp(isoString: string): string {
  if (!isoString) return 'Unknown time';

  const date = new Date(isoString);

  // Validate date
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  // Strict 24h "Hour:Minute" format (e.g., "12:12")
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Formats a date string to HH:mm (24h format)
 * @param isoString
 * @returns string like "12:12"
 */
export function formatMessageTime(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24h format (e.g., 12:12)
  });
}

/**
 * Formats a duration in seconds as "m:ss" (or "mm:ss" when padMinutes is set).
 * Returns the zero value ("0:00" / "00:00") for non-finite or negative input.
 * @param seconds Duration in seconds.
 * @param options.padMinutes Zero-pad the minutes to two digits (default false).
 */
export function formatDuration(seconds: number, options?: { padMinutes?: boolean }): string {
  const padMinutes = options?.padMinutes ?? false;
  if (!Number.isFinite(seconds) || seconds < 0) return padMinutes ? '00:00' : '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const mm = padMinutes ? mins.toString().padStart(2, '0') : mins.toString();
  return `${mm}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Formats large numbers into compact form (e.g., 1.2k, 3.4M).
 */
export function formatCompactNumber(num: number): string {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
}

/**
 * Formats a EUR amount with the € symbol and up to 2 decimal places.
 * @param amount Numeric value in EUR (e.g. 0.42)
 * @returns Display string like "€0.42"
 */
export function formatEur(amount: number): string {
  return `€${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/**
 * Gets the full display name for a user using strict first_name/last_name.
 * Returns 'Guest' if both are missing.
 */
export function getUserFullName(user: User | null | undefined): string {
  if (!user) return 'Guest';
  const first = (user.first_name ?? '').trim();
  const last = (user.last_name ?? '').trim();
  const parts = [first, last].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Guest';
}

/**
 * Gets initials from first_name and last_name only.
 * Returns 'GU' if unavailable.
 */
export function getUserInitials(user: User | null | undefined): string {
  if (!user) return 'GU';
  const first = user.first_name?.trim().charAt(0) ?? '';
  const last = user.last_name?.trim().charAt(0) ?? '';
  const comb = (first + last).toUpperCase();
  return comb.length > 0 ? comb : 'GU';
}
